import { useEffect, FunctionComponent, useState } from 'react';
import { IconButton, Button, Chip, Paper, Divider, Input, Dialog, Table, TableRow, TableCell } from '../../tailwindcomponents';
import { Collection } from '../../components';
import { FlightScheduleLegend } from '../../components/Line';
import { FlightSchedule, LineWorkPackages } from '../../types/line';
import lineApi from '../../api/line';
import EmployeeSearch from '../../components/EmployeeSearch/EmployeeSearch';
import { User } from '../../types/user';
import { FlightTakeoff, Delete } from '../../components/icons';
import { redirectToMxi } from './../../config/mxiProvider';
import { Link } from '../../components/icons';
import { DateTime } from 'luxon';
import { TableColumns } from '../../types/table';

interface LineHandoverProps {
    refresh: Function,
    flights: Array<FlightSchedule>,
    dataFetched: string
    isLineSupervisorAdmin: Boolean
}

const sortTableColumns = [
    { label: " ", key: "index", sortable: false },
    { label: "Tail", key: "tailNumber" },
    { label: "Curr Airport", key: "currentAirport" },
    { label: "Stand", key: "currentStand" },
    { label: "Arr Fl, nr.", key: "arrivalFlightNumberAndDepartureAirport" },
    { label: "Arrival", key: "arrivalTime" },
    { label: "Departure", key: "departureTime" },
    { label: "Dep Fl. nr.", key: "departureFlightNumberAndDepartureAirport" },
    { label: "Mechanics", key: "mechanics", sortable: false },
    { label: "Remarks", key: "remarks" },
] as TableColumns[];

const LineFlightSchedule: FunctionComponent<LineHandoverProps> = ({ refresh, flights, dataFetched, isLineSupervisorAdmin }) => {
    const [remarksDialogOpen, setRemarksDialogOpen] = useState(false);
    const [remark, setRemark] = useState<string>("");
    const [mechanicsDialogOpen, setMechanicsDialogOpen] = useState(false);
    const [tailNumber, setTailNumber] = useState("");
    const [selectedEmployees, setSelectedEmployees] = useState<User[]>([]);
    const [mechanicsChanged, setMechanicsChanged] = useState(false);
    const [paperWorkDialogOpen, setPapaerWorkDialogOpen] = useState(false);
    const [paperWorkPackages, setPaperWorkPackages] = useState(Array<LineWorkPackages>());

    useEffect(() => {
        //Using keyup instead of keydown to avoid multiple fetch calls
        const handleKeyUp = (event: KeyboardEvent) => R_key_Pressed(event);
        document.addEventListener('keyup', handleKeyUp, true);
        
        return () => {
            document.removeEventListener('keyup', handleKeyUp, true);
        }
    }, [remarksDialogOpen, mechanicsDialogOpen]); // Include state variables as dependencies
    
    const R_key_Pressed = (event: any) => {
        const keyCode = event.keyCode;
        if(remarksDialogOpen === false && mechanicsDialogOpen === false){
            if (keyCode === 82) {
                event.preventDefault();
                refresh();
            }
        }
    }
    const generateTablePaperworkBody = (row: LineWorkPackages, index: number) => {
        return (
            <TableRow>
                <TableCell>{index}</TableCell>
                <TableCell onClick={() => redirectToMxi(row.wo_barcode)}><IconButton><Link /></IconButton></TableCell>
                <TableCell>{row.wo_status}</TableCell>
                <TableCell>{row.wo_name}</TableCell>
                <TableCell>{row.wo_number}</TableCell>
                <TableCell>{row.start_date_text}</TableCell>
            </TableRow>
        )
    }
    const generateTableBody = (row: FlightSchedule, index: number) => {
        const dotted = row.wo_status_for_aircraft.status === "Incomplete paperwork";
        const onRemarksClicked = (index: number) => {
            if (isLineSupervisorAdmin) {

                setRemark(row.remarks);
                setTailNumber(row.tailNumber);
                setRemarksDialogOpen(true);
            }
        }

        const onMechanicsClicked = (index: number) => {
            if (isLineSupervisorAdmin) {
                setTailNumber(row.tailNumber);
                setSelectedEmployees(row.assigned_mechanics);
                setMechanicsChanged(false);
                setMechanicsDialogOpen(true);
            }
        }
        const onTailNumberClicked = (row: FlightSchedule) => {
            if (row.wo_status_for_aircraft?.lineWorkpackages?.length > 0) {
                setPapaerWorkDialogOpen(true);
                setTailNumber(row.tailNumber);
                setPaperWorkPackages(row.wo_status_for_aircraft.lineWorkpackages);
            }
        }
        return (
            <TableRow
                // AOG uses red-300 (the Tool Stock overdue red) on purpose - a product decision, not drift.
                className={(row.wo_status_for_aircraft.status === "In Air" || row.wo_status_for_aircraft.status === "Incomplete paperwork") ? "bg-[lightyellow]" :
                    row.wo_status_for_aircraft.status === "In Work" ? "bg-[lightblue]" :
                        row.wo_status_for_aircraft.status === "Closed" ? "bg-[#63AB62]" :
                            row.wo_status_for_aircraft.status === "AOG" ? "bg-red-300" : ""
                }
            >
                <TableCell className={dotted ? "border-t-2 border-b-2 border-l-2 border-dashed border-[gray]" : ""}>{index}</TableCell>
                <TableCell className={dotted ? "border-t-2 border-b-2 border-dashed border-[gray]" : ""} onClick={() => onTailNumberClicked(row)}>{row.tailNumber}</TableCell>
                <TableCell className={dotted ? "border-t-2 border-b-2 border-dashed border-[gray]" : ""}>
                    {row.currentAirport === "IN-AIR" ? <FlightTakeoff /> : row.currentAirport
                    }
                </TableCell>
                <TableCell className={dotted ? "border-t-2 border-b-2 border-dashed border-[gray]" : ""}>{row.currentStand}</TableCell>
                <TableCell className={dotted ? "border-t-2 border-b-2 border-dashed border-[gray]" : ""}>{row.arrivalFlightNumberAndDepartureAirport}</TableCell>
                <TableCell className={dotted ? "border-t-2 border-b-2 border-dashed border-[gray]" : ""}>{row.arrivalTime ? DateTime.fromJSDate(new Date(row.arrivalTime)).toFormat('dd.MM.yyyy HH:mm') : ''}</TableCell>
                <TableCell className={dotted ? "border-t-2 border-b-2 border-dashed border-[gray]" : ""}>{row.departureTime ? DateTime.fromJSDate(new Date(row.departureTime)).toFormat('dd.MM.yyyy HH:mm') : ''}</TableCell>
                <TableCell className={dotted ? "border-t-2 border-b-2 border-dashed border-[gray]" : ""}>{row.departureFlightNumberAndDepartureAirport}</TableCell>
                <TableCell className={dotted ? "border-t-2 border-b-2 border-dashed border-[gray]" : ""} onClick={() => onMechanicsClicked(index)}>
                    <div className="flex flex-wrap items-center gap-1">
                        {row.assigned_mechanics.map((item, idx) => (
                            <Chip key={idx} size="small" color="primary" label={item.abbreviation ? item.abbreviation : "?"} />
                        ))}
                    </div>
                </TableCell>
                <TableCell className={dotted ? "border-t-2 border-b-2 border-r-2 border-dashed border-[gray]" : ""} onClick={() => onRemarksClicked(index)}>{row.remarks}</TableCell>
            </TableRow>
        )
    }

    const onRemoveAllEmployees = () => {
        if (selectedEmployees.length === 0) return;
        Promise.all(selectedEmployees.map(mechanic =>
            lineApi.RemoveEmployeeFlightSchedule(tailNumber, mechanic.user_id)
        )).then(() => {
            setSelectedEmployees([]);
            setMechanicsChanged(true);
        });
    }

    const onSelectedEmployeeRow = (employee: User | null, type: string) => {
        if (type === "Assign" && employee !== null) {
            lineApi.AssignEMployeeFlightSchedule(employee, tailNumber).then(res => {
                if(res.status === true){
                    setSelectedEmployees([...selectedEmployees, employee]);
                    setMechanicsChanged(true);
                }
            });
        }
    }

    const onCloseMechanicDialog = () => {
        setMechanicsDialogOpen(false);
        // Only refetch when something actually changed - opening and closing the
        // dialog to look at the roster shouldn't reload the whole schedule.
        if (mechanicsChanged) {
            refresh();
        }
        setMechanicsChanged(false);
    }

   const onRemoveEmployee = (employeeId: number) => {
        const employeeToRemove = selectedEmployees.findIndex(employee => employee.user_id === employeeId);
        const tempEmployees = [...selectedEmployees];
        tempEmployees.splice(employeeToRemove, 1);
        setSelectedEmployees(tempEmployees);
        lineApi.RemoveEmployeeFlightSchedule(tailNumber, employeeId).then(res => { setMechanicsChanged(true); });
    }

    const onUpdateRemarkToFlightSchedule = (status: boolean) => {
        if (status === true) {
            lineApi.UpdateFlightSchedule(remark, tailNumber).then(res => {
                setRemark("");
                refresh();
            });
        }
        setRemarksDialogOpen(false);
    }

    const onRemoveAllofLineShiftSupervisorEmployees = () => {
        lineApi.RemoveAllofLineShiftSupervisorEmployees().then(res => {
            refresh();
        });
    }
    return (
        <div>
            <FlightScheduleLegend
                dataFetched={dataFetched}
                onRemoveAllEmployees={isLineSupervisorAdmin ? () => onRemoveAllofLineShiftSupervisorEmployees() : undefined}
            />
            <Table
                rows={flights}
                generateBody={(row: any, index: number) => generateTableBody(row, index)}
                pagination={false}
                skipIndexCol={true}
                sortTableColumns={sortTableColumns}
                isFlightSchedule={true}
                secondarySortKey="arrivalTime"
            />
            <Dialog
                visible={mechanicsDialogOpen}
                max_width={"md"}
                title={""}
                hideCloseButton
                context={
                    <div>
                        <div className="w-full content-center items-center">
                            <Paper elevation={3} className="p-1.5 m-1">
                                <EmployeeSearch autoFocusOnInput searchByAbbreviation place_holder={"Search by abbreviation"} clearOnSelect fullUser={true} onSelectedEmployee={(employee: User | null) => onSelectedEmployeeRow(employee, "Assign")} />
                            </Paper>
                            <Divider className="mt-[26px] mb-6" />
                            <Paper elevation={3} className="p-1.5 m-1">
                                <EmployeeSearch place_holder={"Search by name"} clearOnSelect fullUser={true} onSelectedEmployee={(employee: User | null) => onSelectedEmployeeRow(employee, "Assign")} />
                            </Paper>
                            <Collection background={false} collectionList={selectedEmployees} title="Employees selected" idProperty="user_id" removeItem={(itemId: number) => onRemoveEmployee(itemId)} />
                            {selectedEmployees.length > 0 &&
                                <div className="flex flex-wrap justify-end">
                                    <Button variant="text" color="error" size="small" startIcon={<Delete />} onClick={() => onRemoveAllEmployees()}>Remove all employees</Button>
                                </div>
                            }
                        </div>
                    </div>
                }
                onClose={(status: any) => onCloseMechanicDialog()}
                strict={false}
                show_options={false}
            />
            <Dialog
                visible={remarksDialogOpen}
                max_width={"sm"}
                title={"Remarks"}
                context={
                    <div>
                        <Input
                            multiline={true}
                            help_text={""}
                            label_text={"Remarks"}
                            value={remark}
                            onTextChange={(text: string) => setRemark(text)}
                            type="text"
                            width={100}
                        />
                    </div>
                }
                onClose={(status: any) => onUpdateRemarkToFlightSchedule(status)}
                strict={false}
                show_options={true}
            />
            <Dialog
                visible={paperWorkDialogOpen}
                max_width={"md"}
                title={`Paperwork for : ${tailNumber}`}
                context={
                    <div>
                        <Table
                            headers={["Mxi", "Status", "Wo name", "Wo number", "start"]}
                            rows={paperWorkPackages}
                            generateBody={(row: any, index: number) => generateTablePaperworkBody(row, index)}
                            pagination={false}
                            skipIndexCol={false}
                        />
                    </div>
                }
                onClose={(status: any) => { setPapaerWorkDialogOpen(false); setPaperWorkPackages([]) }}
                strict={false}
                show_options={false}
            />
        </div>
    )
}


export default LineFlightSchedule;